/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React from 'react';
import { AppFlavour, getBrandingConfig } from '../../../config/app-config';
import PTLogo from '../../../images/Portfolio_Tracker_logo.svg';
import SDGAPLogo from '../../../images/SDGAP_logo.svg';
// import CCLogo from '../../../images/Carbon_Calculator_logo.svg';
import WTLogo from '../../../images/World_tracker_logo.svg';
import EngineBLogo from '../../../images/engineB_172px_w.png';
import GrowthActivistsLogo from '../../../images/growth_activists_logo-125px_w.png';
import PlanetGroupsLogo from '../../../images/planet-groups-logo-172_w.png';
import FinncapLogo from '../../../images/finncap_logo_125px_w.png';
import BoBEarthLogo from '../../../images/BoBEarth_Logo_125px.png';
import BubkaLogo from '../../../images/Bubka_Logo_125px.png';
import POVLogo from '../../../images/POV_Logo_125px.png';
import SASBLogo from '../../../images/SASB_logo.svg';
import ClayOPSLogo from '../../../images/ClayOPS_Logo.png';
import GRILogo from '../../../images/GRI_logo.svg';
import IMALogo from '../../../images/IMA_Logo_125px.png';
import UNGCLogo from '../../../images/ungc_logo_125px.png';
import WWGLogo from '../../../images/wwg-logo-landscape.svg';
import MALogo from '../../../images/Materiality_Assessment_logo.svg';
import EmissionsLogo from '../../../images/Emissions_Tracker_logo.svg';
import ProjectTrackerLogo from '../../../images/Project_Tracker_logo.svg';
import CityTrackerLogo from '../../../images/City_Tracker_logo.svg';
import NationTrackerLogo from '../../../images/Nation_Tracker_logo.svg';

// import CDPLogo from '../../../images/Carbon_Disclosure_Project_logo.svg';
import TCFDLogo from '../../../images/TCFD_logo.png';
import FutureFitLogo from '../../../images/future-fit-business-logo.svg';
import EcoAgeLogo from '../../../images/EcoAge_Logo.png';
import SmartKarmaLogo from '../../../images/smartkarma.jpeg';
import AVPNLogo from '../../../images/AVPN_Logo.jpg';
import LeQuantoLogo from '../../../images/LeQuanto_Logo.png';
import CodeEngineLogo from '../../../images/CodeEngine_Logo.png';
import NovALogo from '../../../images/NovA_Logo.png';
import Green2Gold from '../../../images/green2gold.png';
import AssuranceTrackerLogo from '../../../images/Assurance_Tracker.svg';
import KyndrylLogo from '../../../images/kyndryl_logo.jpg';
import MomentaLogo from '../../../images/momenta_logo.png';
import { ReferralInfoType } from '../../../types/onboarding';

enum PartnerSectionCodes {
  Consultancy = 'consultancy',
  Fundraising = 'fundraising',
  TechnologyPartners = 'technology-partners',
  StandardBoards = 'standard-boards',
  MemberInstitutions = 'member-institutions',
}

export const partnerSections: {
  code: PartnerSectionCodes,
  title: string
}[] = [
    {
      code: PartnerSectionCodes.Consultancy,
      title: 'Consultancy / professional services',
    },
    {
      code: PartnerSectionCodes.Fundraising,
      title: 'Fundraising / Incubator',
    },
    {
      code: PartnerSectionCodes.TechnologyPartners,
      title: 'Technology partners',
    },
    {
      code: PartnerSectionCodes.StandardBoards,
      title: 'Standard & framework boards',
    },
    {
      code: PartnerSectionCodes.MemberInstitutions,
      title: 'Member Institutions',
    },
  ];

type handlerFunc = (card?: Card, referralInfo?: ReferralInfoType) => void;
export interface ClickHandlers {
  GoToCompanyTrackerPro: handlerFunc;
  GoToCompanyTrackerLight: handlerFunc;
  GoToCompanyTrackerRegistration: (options?: { onboardingPath?: string, referralInfo?: ReferralInfoType }) => void;
  GoToCompanyTrackerRegistrationDirect: handlerFunc;
  GoToCompanyTrackerLightInfo: handlerFunc;
  GoToPortfolioTracker: handlerFunc;
  GoToAssurance: handlerFunc;
  GoToSDGAP: handlerFunc;
  GoToWorldTracker: handlerFunc;
  ShowRequestADemo: handlerFunc;
}

export interface Card {
  logo: string;
  title: JSX.Element;
  subtitle?: JSX.Element;
  description?: JSX.Element;
  url?: string;
  clickHandler?: keyof ClickHandlers;
  spotlight?: boolean;
  solutions?: boolean;
  comingSoon?: boolean;
  partners?: PartnerSectionCodes;
  btnOutline?: boolean;
  slug?: string;
  learnMoreLink?: string;
  tags: string[];
  actionTextSecondary?: string
  isProduct?: boolean;
  isStaffOnly?: boolean;
  sponsoredByLogo?: string;
  button?: {
    text: string;
    onClickHandler?: keyof ClickHandlers;
    disabled?: boolean;
  }
}

const sgxApp = getBrandingConfig(AppFlavour.SINGAPORE).branding.ctlApp;
const ctlApp = getBrandingConfig(AppFlavour.DEFAULT).branding.ctlApp;

export const getCards = (isLoggedIn: boolean): Card[] => [
  {
    logo: sgxApp.cardLogo,
    title: <>{sgxApp.name}</>,
    subtitle: <>{sgxApp.subtitle}</>,
    description: <>{sgxApp.description}</>,
    tags: [AppFlavour.SINGAPORE],
    spotlight: true,
    solutions: true,
    clickHandler: 'GoToCompanyTrackerLight',
    button: {
      text: isLoggedIn ? 'Access now' : 'Sign up',
      onClickHandler: isLoggedIn ? 'GoToCompanyTrackerLight' : 'GoToCompanyTrackerLightInfo',
    },
    btnOutline: false,
    slug: sgxApp.productSlug,
    actionTextSecondary: 'Buy now',
    isProduct: true,
    sponsoredByLogo: sgxApp.sponsoredByLogo ?? WWGLogo,
  },
  {
    logo: ctlApp.cardLogo,
    title: <>{ctlApp.name}</>,
    subtitle: <>{ctlApp.subtitle}</>,
    description: <>{ctlApp.description}</>,
    tags: [AppFlavour.SINGAPORE, AppFlavour.DEFAULT],
    spotlight: true,
    solutions: true,
    clickHandler: 'GoToCompanyTrackerLight',
    button: {
      text: isLoggedIn ? 'Access now' : 'Sign up',
      onClickHandler: isLoggedIn ? 'GoToCompanyTrackerLight' : 'GoToCompanyTrackerRegistrationDirect',
    },
    btnOutline: false,
    slug: ctlApp.productSlug,

    learnMoreLink: 'https://companytracker.g17.eco/',
    isProduct: true,
    sponsoredByLogo: ctlApp.sponsoredByLogo ?? WWGLogo,
  },
  // {
  //   logo: CCLogo,
  //   title: <>Carbon Calculator</>,
  //   subtitle: <>Calculate your total energy usage and your company’s carbon emissions</>,
  //   description: <>This helps you calculate your direct emissions (scope 1) and those emissions from purchased
  //     electricity (scope 2), as well an indirect emissions (scope 3) if needed. The tool will also calculate your
  //     total energy usage as well as energy usage per employee and per m².</>,
  //   tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
  //   solutions: true,
  //   clickHandler: 'GoToCarbonCalculator',
  //   btnOutline: true,
  //   // slug: "carbon-calculator",
  //   sponsoredByLogo: WWGLogo,
  // },
  // hiding CC until further notice
  // {
  //   logo: CCLogo,
  //   title: <>Carbon Calculator</>,
  //   subtitle: <>Calculate your total energy usage and your company’s carbon emissions</>,
  //   description: <>This helps you calculate your direct emissions (scope 1) and those emissions from purchased
  //     electricity (scope 2), as well an indirect emissions (scope 3) if needed. The tool will also calculate your
  //     total energy usage as well as energy usage per employee and per m².</>,
  //   tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
  //   solutions: true,
  //   clickHandler: 'GoToCarbonCalculator',
  //   button: {
  //     text: isLoggedIn ? 'Access now' : 'Log in',
  //   },
  //   btnOutline: true,
  //   // slug: "carbon-calculator",
  //   isProduct: true,
  //   sponsoredByLogo: WWGLogo,
  // },
  {
    logo: AssuranceTrackerLogo,
    title: <>Assurance Tracker</>,
    subtitle: <>An application that allows Assurers to login and assure the Sustainability reports for an organisation
      using Company Tracker Pro.</>,
    description: <>An application that allows Assurers to login and assure the Sustainability reports for
      an organisation using Company Tracker Pro.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    solutions: true,
    clickHandler: 'GoToAssurance',
    button: {
      text: isLoggedIn ? 'Access now' : 'Log in',
    },
    btnOutline: true,
    isProduct: true,
    sponsoredByLogo: WWGLogo,
  },
  {
    logo: PTLogo,
    title: <>Portfolio Tracker</>,
    subtitle: <>How sustainable is your portfolio? Track and measure your companies</>,
    description: <>Delivers the next generation of data, reporting and impact measurement beyond ESG. It enables
      investors to align all types of portfolios and investment classes to globally recognised sustainability
      standards, frameworks and regulation. It will help you understand and see your investments through a holistic
      SEE (social, economic and environmental) lens.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    solutions: true,
    clickHandler: 'GoToPortfolioTracker',
    button: {
      text: isLoggedIn ? 'Access now' : 'Log in',
    },
    btnOutline: true,
    actionTextSecondary: 'Buy now',
    isProduct: true,
    sponsoredByLogo: WWGLogo,
  },
  {
    logo: MALogo,
    title: <>Materiality Assessment</>,
    subtitle: <>Identify and prioritise the most significant economic, environmental, and social issues for your sustainability strategy.</>,
    description: <>Identify and prioritise the most significant economic, environmental, and social issues for your sustainability strategy.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    solutions: true,
    clickHandler: 'ShowRequestADemo',
    button: {
      text: 'Register interest',
    },
    btnOutline: true,
    isProduct: true,
    sponsoredByLogo: WWGLogo,
  },
  {
    logo: EmissionsLogo,
    title: <>Emissions Tracker</>,
    subtitle: <>Integrated emissions tracking and carbon accounting that meets GHG protocol standards, and aligns with regulations.</>,
    description: <>Integrated emissions tracking and carbon accounting that meets GHG protocol standards, and aligns with regulations.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    solutions: true,
    clickHandler: 'ShowRequestADemo',
    button: {
      text: 'Register interest',
    },
    btnOutline: true,
    isProduct: true,
    sponsoredByLogo: WWGLogo,
  },
  {
    logo: SDGAPLogo,
    title: <>SD(G)AP</>,
    subtitle: <>The digital taxonomy - mapping globally recognised standards to the SDG targets.</>,
    description: <>The Sustainable Development Global Accounting Principles SD(G)AP is the digital taxonomy
      database, with a unique visualization of mapping globally recognised standards, frameworks and policies
      to each other and to the backbone of the SDG targets.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    solutions: true,
    clickHandler: 'GoToSDGAP',
    button: {
      text: isLoggedIn ? 'Access now' : 'Log in',
    },
    btnOutline: true,
    isProduct: true,
    sponsoredByLogo: WWGLogo,
  },
  {
    logo: WTLogo,
    title: <>World Tracker</>,
    subtitle: <>See country deficits against the UN Sustainable Development Goals (SDGs)</>,
    description: <>A global visualisation taking in UN data sets to represent the SDG deficits per country. As
      organizations and nations start to onboard, we are able to track and communicate progress on SDG delivery.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    solutions: true,
    clickHandler: 'GoToWorldTracker',
    button: {
      text: 'View',
    },
    btnOutline: false,
    sponsoredByLogo: WWGLogo,
  },
  {
    logo: ProjectTrackerLogo,
    title: <>Project Tracker</>,
    subtitle: <>Empowers companies to enhance their sustainability efforts through comprehensive project monitoring and data-driven insights.</>,
    description: <>Empowers companies to enhance their sustainability efforts through comprehensive project monitoring and data-driven insights.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    comingSoon: true,
    clickHandler: 'ShowRequestADemo',
    button: {
      text: 'Register interest',
    },
    btnOutline: true,
  },
  {
    logo: CityTrackerLogo,
    title: <>City Tracker</>,
    subtitle: <>A comprehensive data collection and monitoring tool to assist cities in tracking and improving their sustainability performance.</>,
    description: <>A comprehensive data collection and monitoring tool to assist cities in tracking and improving their sustainability performance.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    comingSoon: true,
    clickHandler: 'ShowRequestADemo',
    button: {
      text: 'Register interest',
    },
    btnOutline: true,
  },
  {
    logo: NationTrackerLogo,
    title: <>Nation Tracker</>,
    subtitle: <>Designed to monitor and track a nation's progress on SDGs, including utilising data from the City Tracker.</>,
    description: <>Designed to monitor and track a nation's progress on SDGs, including utilising data from the City Tracker.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    comingSoon: true,
    clickHandler: 'ShowRequestADemo',
    button: {
      text: 'Register interest',
    },
    btnOutline: true,
  },
  {
    logo: KyndrylLogo,
    title: <>Kyndryl</>,
    subtitle: (
      <>
        We design, build, manage and modernise the mission-critical technology systems that the world depends on every
        day.
      </>
    ),
    description: (
      <>
        <p>
          We design, build, manage and modernise the mission-critical technology systems that the world depends on every
          day.
        </p>
      </>
    ),
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    url: 'www.kyndryl.com',
    partners: PartnerSectionCodes.TechnologyPartners,
  },
  {
    logo: BoBEarthLogo,
    title: <>BoB Earth</>,
    subtitle: <>BoB Earth is a purpose-led platform designed to bring communities together. A community...</>,
    description: <>BoB Earth is a purpose-led platform designed to bring communities together. A community
      of like-minded peers, united on purpose and ready to kickstart the economy, to shift the world into a
      new era.
      In any type of need or situation, BoB Earth can deliver live connection and communication, by Connecting
      people to Unite, Learning together to Grow and Trading together to create positive impact.</>,
    tags: [AppFlavour.DEFAULT],
    url: 'bobearth.com',
    spotlight: false,
    partners: PartnerSectionCodes.TechnologyPartners,
  },
  {
    logo: BubkaLogo,
    title: <>BUBKA</>,
    subtitle: <>Marketing and advertising agency with a focus on sustainability and impact. Bubka...</>,
    description: <>Marketing and advertising agency with a focus on sustainability and impact. Bubka
      focuses on creating striking ideas that move people.</>,
    tags: [AppFlavour.DEFAULT],
    url: 'www.bubka.be/',
    spotlight: false,
    partners: PartnerSectionCodes.Consultancy,
  },
  {
    logo: MomentaLogo,
    title: <>momenta</>,
    subtitle: <>momenta Group is a performance improvement firm driven to make
      a difference in organisations through their best assets – people.</>,
    description: <>momenta's solutions are designed to incorporate and complement their client’s values, existing processes, procedures and best practices. Developed and delivered by a team of multicultural and multilingual consultants who are backed by years of real-life industry experience, our solutions are built
      on cutting-edge methodology that makes learning stick, real and relevant. Momenta has offices in Singapore, Hong Kong, Malaysia, India, Indonesia, Philippines,
      Vietnam, Taiwan, Japan, China and USA.</>,
    tags: [AppFlavour.SINGAPORE],
    url: 'momenta.biz',
    partners: PartnerSectionCodes.Consultancy,
  },
  {
    logo: ClayOPSLogo,
    title: <>ClayOPS</>,
    subtitle: <>ClayOPS is a wholly owned company of NCS Pte Ltd. Our main core expertise is in data
      engineering and data analytics.</>,
    description: <>
      <p>ClayOPS is a wholly owned company of NCS Pte Ltd. Our main core expertise is in data
        engineering and data analytics. Recently, we have started to focus on growing our ESG data engineering
        practice, working closely with partners and customers. We designed a data processing engine that
        automates the extraction and conformity of ESG related data for submission.</p>
      <p>At ClayOPS, we share the same vision as our partners like Worldwide Generation and their customers;
        that there is a great urgency for a global effort to reduce our carbon footprint.  We hope that by
        automating ESG data processing, we can contribute our expertise in a small way that will leave a
        lasting impact for our future generations.
      </p>
    </>,
    tags: [AppFlavour.SINGAPORE],
    url: 'clayops.com',
    partners: PartnerSectionCodes.TechnologyPartners,
  },
  {
    logo: EngineBLogo,
    title: <>Engine B</>,
    subtitle: <>An audit technology company specialising in AI and data analytics for the professional...</>,
    description: <>An audit technology company specialising in AI and data analytics for the professional
      services industry. Working alongside the audit, legal and tax sectors, our mission is to open up company
      to company interactions with next-generation technology and innovation. With our Audit Common Data Model
      and Knowledge Graphs, we are transforming the way that audit services are delivered.</>,
    tags: [AppFlavour.DEFAULT],
    url: 'www.engineb.com',
    spotlight: true,
    partners: PartnerSectionCodes.TechnologyPartners,
  },
  {
    logo: CodeEngineLogo,
    title: <>Code Engine Studio</>,
    subtitle: <>Code Engine Studio is a software development partner specialized in creating customized software solutions...</>,
    description: <>Code Engine Studio is a software development partner specialized in creating customized
      software solutions that address today’s challenges and tomorrow’s opportunities with an eye on sustainability.
      Our diverse team of experts are here to help you to unearth untapped value, and create amazing and
      measurable digital experiences that deliver results.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    url: 'www.codeenginestudio.com',
    spotlight: true,
    partners: PartnerSectionCodes.TechnologyPartners,
  },
  {
    logo: NovALogo,
    title: <>NovA!</>,
    subtitle: <>An audit technology company specialising in AI and data analytics for the professional...</>,
    description: <>Project NovA! is a private-public initiative led by MAS to catalyze the growth of
      sustainable financing via the use of verifiable sources of data and technology.  Project NovA!
      serves as a powerful data intelligence and analytics platform and aims to strengthen Singapore's
      reputation as a global hub for sustainable financing starting with efforts to combat greenwashing.</>,
    tags: [AppFlavour.SINGAPORE],
    spotlight: true,
    url: 'www.aicadium.ai',
    partners: PartnerSectionCodes.TechnologyPartners,
  },
  {
    logo: FinncapLogo,
    title: <>finnCap</>,
    subtitle: <>Strategic Advisory and Capital Raising Services. finnCap is an entrepreneurial...</>,
    description: <>Strategic Advisory and Capital Raising Services.
      finnCap is an entrepreneurial, employee-owned, award-winning broker and financial adviser. We advise on,
      promote and provide access to capital for ambitious, fast-growth companies in both the private and public
      equity markets, with outstanding client service..</>,
    tags: [AppFlavour.DEFAULT],
    url: 'www.finncap.com',
    spotlight: false,
    partners: PartnerSectionCodes.Fundraising,
  },
  {
    logo: GrowthActivistsLogo,
    title: <>Growth Activists</>,
    subtitle: <>The Growth Activists (Australia- Covers Asia Pacific Region). We are a growth...</>,
    description: <>The Growth Activists (Australia- Covers Asia Pacific Region)
      We are a growth strategy consultancy that activates ambitious businesses to maximise their value.
      We are experienced business leaders with a track record of making bold winning moves.
      We put purpose at the centre of strategy to align everyone who matters to deliver a result that is bigger
      than the sum of its parts.
      We exist in order to fulfil the potential of people and businesses to succeed and do good.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    url: 'www.growthactivists.com',
    spotlight: false,
    partners: PartnerSectionCodes.Consultancy,
  },
  {
    logo: IMALogo,
    title: <>IMA</>,
    subtitle: <>IMA, the association of accountants and financial professionals in business, is one of the...</>,
    description: <>IMA, the association of accountants and financial professionals in business, is one of the
      largest and most respected associations focused exclusively on advancing the management accounting profession.
      Globally, IMA supports the profession through research, the CMA (Certified Management Accountant) program,
      continuing education, and advocacy of the highest ethical business practices.</>,
    tags: [AppFlavour.DEFAULT],
    url: 'www.imanet.org',
    spotlight: false,
    partners: PartnerSectionCodes.MemberInstitutions,
  },
  {
    logo: PlanetGroupsLogo,
    title: <>Planet Groups</>,
    subtitle: <>An international non-profit consultancy based in Germany, the UK and Switzerland...</>,
    description: <>An international non-profit consultancy based in Germany, the UK and Switzerland.
      Our mission is to support employees who want to take climate action in their own companies, and
      to help sustainability teams and company leadership to optimise buy-in for their organisation’s
      climate transformation, thereby driving innovation and future-proofing their company.</>,
    tags: [AppFlavour.DEFAULT],
    url: 'www.planetgroups.net',
    spotlight: true,
    partners: PartnerSectionCodes.Consultancy,
  },
  {
    logo: POVLogo,
    title: <>POV</>,
    subtitle: <>Point Off View (POV) is a mission-driven agency focused on digital communication and...</>,
    description: <>Point Off View (POV) is a mission-driven agency focused on digital communication and
      sustainable transformation. Founded by Marina Testino in 2017, POV was created as a sustainable
      fashion label. Since then, it has evolved into an experiential creative hub which also provides agency
      services driven by Testino's unique POV, merging digital content creation and innovative transformation
      services for brands, creatives and organizations. POV works across art, fashion and environmental activism
      to forge forward-thinking partnerships, bring people closer through creativity and generate compelling
      visual campaigns and narratives for those seeking an alternative #pointoffview..</>,
    tags: [AppFlavour.DEFAULT],
    url: 'www.pointoffview.com',
    spotlight: false,
    partners: PartnerSectionCodes.MemberInstitutions,
  },
  {
    logo: GRILogo,
    title: <>GRI</>,
    subtitle: <>GRI is an independent international organization that has pioneered sustainability reporting...</>,
    description: <>GRI is an independent international organization that has pioneered sustainability reporting
      since 1997. GRI helps businesses and governments worldwide understand and communicate their impact on
      critical sustainability issues such as climate change, human rights, governance and social well-being.
      This enables real action to create social, environmental and economic benefits for everyone.
      The GRI Sustainability Reporting Standards are developed with true multi-stakeholder contributions
      and rooted in the public interest.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    url: 'www.globalreporting.org',
    spotlight: false,
    partners: PartnerSectionCodes.StandardBoards,
  },
  {
    logo: SASBLogo,
    title: <>SASB</>,
    subtitle: <>SASB Standards enable businesses around the world to identify, manage and communicate...</>,
    description: <>SASB Standards enable businesses around the world to identify, manage and communicate
      financially-material sustainability information to investors. Available for 77 industries, the Standards
      identify the subset of environmental, social, and governance issues most relevant to financial performance.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    url: 'www.sasb.org',
    spotlight: false,
    partners: PartnerSectionCodes.StandardBoards,
  },
  {
    logo: UNGCLogo,
    title: <>UNGC</>,
    subtitle: <>As a special initiative of the UN Secretary-General, the United Nations Global Compact is...</>,
    description: <>As a special initiative of the UN Secretary-General, the United Nations Global Compact is
      a call to companies everywhere to align their operations and strategies with Ten Principles in the areas
      of human rights, labour, environment and anti-corruption. Our ambition is to accelerate and scale the
      global collective impact of business by upholding the Ten Principles and delivering the Sustainable
      Development Goals through accountable companies and ecosystems that enable change. With more than 12,000
      companies and 3,000 non-business signatories based in over 160 countries, and 69 Local Networks, the UN
      Global Compact is the world’s largest corporate sustainability initiative — one Global Compact uniting
      business for a better world.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    url: 'www.unglobalcompact.org',
    spotlight: false,
    partners: PartnerSectionCodes.StandardBoards,
  },
  // {
  //   logo: CDPLogo,
  //   title: <>CDP</>,
  //   subtitle: <>A global non-profit that runs the world’s environmental disclosure system.</>,
  //   description: <>A global non-profit that runs the world’s environmental disclosure system.</>,
  //   tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
  //   url: 'www.cdp.net/en',
  //   spotlight: false,
  //   partners: PartnerSectionCodes.StandardBoards,
  // },
  {
    logo: TCFDLogo,
    title: <>TCFD</>,
    subtitle: <>TCFD provides climate-related ﬁnancial disclosure recommendations designed to help...</>,
    description: <>TCFD provides climate-related ﬁnancial disclosure recommendations designed to help companies provide better information to support informed capital allocation. The TCFD disclosure recommendations are structured around four thematic areas that represent core elements of how companies operate: governance, strategy, risk management, and metrics and targets. The four recommendations are interrelated and supported by 11 recommended disclosures that build out the framework with information that should help investors and others understand how reporting organizations think about and assess climate-related risks and opportunities.</>,
    tags: [AppFlavour.DEFAULT, AppFlavour.SINGAPORE],
    url: 'www.fsb-tcfd.org',
    spotlight: false,
    partners: PartnerSectionCodes.StandardBoards,
  },
  {
    logo: FutureFitLogo,
    title: <>Future-Fit </>,
    subtitle: <>Committed to an open, transparent and collaborative development process, a free-to-use licensing model, and a partnership approach.</>,
    description: <>Future-Fit’s mission is to catalyze a shift in the way the global economy works – by translating systems
      science into practical, free-to- use tools designed to help business leaders, investors and policy makers respond today’s
      biggest challenges. Future-Fit builds tools that equip people with the means to recognize, reward and steer the creation
      of value across the socioeconomic system as a whole. Future-Fit is committed to an open, transparent and collaborative
      development process, a free-to-use licensing model, and a partnership approach designed to empower those who share their vision.</>,
    tags: [AppFlavour.DEFAULT],
    url: 'www.futurefitbusiness.org',
    spotlight: false,
    partners: PartnerSectionCodes.StandardBoards
  },
  {
    logo: EcoAgeLogo,
    title: <>Eco-Age</>,
    subtitle: <>Help brands to lower their impact on people and planet, and communicate their journey in a transparent, tangible way.</>,
    description: <>Help brands to lower their impact on people and planet, and communicate their journey in a transparent, tangible way.
      From strategic consultancy all the way through to media relations, corporate reputation and digital storytelling, the Eco-Age team
      combines technical knowledge with creativity and innovation. Eco-Age is widely recognised as the authority on sustainability in the
      industry and beyond.</>,
    tags: [AppFlavour.DEFAULT],
    url: 'www.eco-age.com',
    spotlight: false,
    partners: PartnerSectionCodes.Consultancy
  },
  {
    logo: SmartKarmaLogo,
    title: <>Smartkarma</>,
    subtitle: <>Smartkarma is the Independent Investment Research Network, uniting Insight Providers, Investors, and Investor Relations Professionals.</>,
    description: <><p>Smartkarma is the Independent Investment Research Network, uniting Insight Providers, Investors, and Investor
      Relations Professionals.</p>
      <p>Smartkarma serves 250+ global institutional investors, 2500+ corporates, leading exchanges and sellside through targeted
        capital-market solutions, delivered by its proprietary cloud-based platform. Smartkarma offers a market-leading Corporate
        IR platform, which complements the realtime sustainability reporting capabilities offered by WWG.</p>
    </>,
    tags: [AppFlavour.SINGAPORE],
    url: 'www.smartkarma.com',
    spotlight: false,
    partners: PartnerSectionCodes.Consultancy
  },
  {
    logo: AVPNLogo,
    title: <>AVPN</>,
    subtitle: <>We are a leading ecosystem builder that is increasing the flow of financial, human, and intellectual capital...</>,
    description: <>We are a leading ecosystem builder that is increasing the flow of financial, human, and intellectual capital from
      Asia and around the world into the social sector in Asia. We provide a network of peers, rigorous learning programmes, and
      innovative capital mobilization opportunities that make sure resources are more effectively deployed.</>,
    tags: [AppFlavour.SINGAPORE],
    url: 'avpn.asia',
    spotlight: false,
    partners: PartnerSectionCodes.Consultancy,
  },
  {
    logo: LeQuantoLogo,
    title: <>LeQuanto</>,
    subtitle: <>We specialize in the areas of Predictive Analytics and Performance (Financial & Non-Financial) Forecast and...</>,
    description: <>We specialize in the areas of Predictive Analytics and Performance (Financial & Non-Financial) Forecast and
      Attribution to help organizations drive their business performances. We provide end-to-end implementation through policies
      and internal controls, as well as operationalization and sustenance of the processes through our customizable
      Software-As-A-Service (SaaS) platforms.</>,
    tags: [AppFlavour.SINGAPORE],
    url: 'lequanto.com',
    spotlight: false,
    partners: PartnerSectionCodes.Consultancy,
  },
  {
    logo: Green2Gold,
    title: <>Green2Gold</>,
    subtitle: <>Green2Gold is an impact incubator, which has been operating since 1970, and works with inventors & entrepreneurs to
      commercialize inventions and business concepts.</>,
    description: <>Green2Gold unites highly experienced business advisers, cutting edge engineers, award-winning designers, innovative
      developers, state of the art manufacturers, international practicing attorneys, world responsible trusted financial experts, creative
      professional educators, leading scientists, wide spectrum marketing and licensing established relationships, world helping inventors,
      innovators, social ventures, and non-profit founders.</>,
    tags: [AppFlavour.DEFAULT],
    url: 'www.green2gold.org',
    spotlight: false,
    partners: PartnerSectionCodes.Fundraising
  },
]

export const getAppFlavourCards = (appFlavour: AppFlavour, isLoggedIn: boolean, isUserStaff: boolean) => {
  return getCards(isLoggedIn)
    .filter(card => {
      if (card.isStaffOnly) {
        if (!isUserStaff) {
          return false;
        }
      }
      return card.tags.includes(appFlavour)
    });
}
